import { Link, useLocation } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { useProject } from '../contexts/ProjectContext'
// import hdccLogo from '../assets/images/HDCC_logo.ico'
import logo from "../assets/images/Hdcc_logo.jpg";

const DashboardNav = ({ user, onLogout, setIsSidebarOpen, projectsModalOpen, setProjectsModalOpen }) => {
  const location = useLocation()

  const handleHamburgerClick = (e) => {
    e.stopPropagation() // Prevent event from bubbling to app-main
    console.log('Hamburger clicked!')
    setIsSidebarOpen(prev => {
      console.log('Setting sidebar from', prev, 'to', !prev)
      return !prev
    })
  }

  const {
    projects,
    selectedProject,
    selectProject,
    locations,
    selectedLocation,
    selectLocation,
    loading: projectsLoading,
    locationsLoading,
    projectPendingCounts
  } = useProject()

  // Searchable dropdown state
  const [projectSearchOpen, setProjectSearchOpen] = useState(false)
  const [projectSearchTerm, setProjectSearchTerm] = useState('')
  const [showAllProjects, setShowAllProjects] = useState(false)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const projectDropdownRef = useRef(null)
  const userDropdownRef = useRef(null)

  // Open project dropdown when triggered from sidebar
  useEffect(() => {
    if (projectsModalOpen) {
      setProjectSearchOpen(true)
      setProjectsModalOpen(false) // Reset the flag
    }
  }, [projectsModalOpen, setProjectsModalOpen])

  // Get user initials
  const getUserInitials = () => {
    if (!user) return 'U'
    const name = user.name || user.preferred_username || 'User'
    const nameParts = name.split(' ')
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  // Calculate total notification count
  const totalNotifications = Object.values(projectPendingCounts).reduce((sum, count) => sum + count, 0)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setProjectSearchOpen(false)
        setProjectSearchTerm('')
        setShowAllProjects(false)
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter projects based on search term
  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
  )

  // Get recent projects (top 3 by pending count or most recently selected)
  const recentProjects = projects
    .filter(p => projectPendingCounts[p.id] > 0)
    .sort((a, b) => (projectPendingCounts[b.id] || 0) - (projectPendingCounts[a.id] || 0))
    .slice(0, 3)

  // Helper function to get project category
  const getProjectCategory = (projectName) => {
    if (projectName.toLowerCase().includes('equipment') || projectName.toLowerCase().includes('fuel')) {
      return 'Fuel'
    } else if (projectName.toLowerCase().includes('cost')) {
      return 'Cost Module'
    }
    return 'Project'
  }

  return (
    <>
      <nav className="dashboard-nav">
        {/* Logo */}
        <div className="nav-left">
          <button className="mobile-menu-button-header" onClick={handleHamburgerClick} type="button">
            ☰
          </button>
          <img src={logo} alt="Hawaiian Dredging" className="header-logo" />
        </div>

        <div className="nav-right">
          {/* Project Selector - Searchable Dropdown */}
          {user && projects.length > 0 && (
            <div className="project-selector" ref={projectDropdownRef}>
              <div className="searchable-select-container">
                <button
                  className="searchable-select-trigger"
                  onClick={() => setProjectSearchOpen(!projectSearchOpen)}
                  disabled={projectsLoading}
                  type="button"
                >
                  <span className="project-icon-trigger">💼</span>
                  <span className="searchable-select-value">
                    {selectedProject?.name || 'Cost Module'}
                  </span>
                  <span className="searchable-select-arrow">▼</span>
                </button>

                {projectSearchOpen && (
                  <div className="searchable-select-dropdown">
                    <div className="searchable-select-search">
                      <span className="search-icon">🔍</span>
                      <input
                        type="text"
                        placeholder="Search projects or applications..."
                        value={projectSearchTerm}
                        onChange={(e) => setProjectSearchTerm(e.target.value)}
                        className="searchable-select-input"
                        autoFocus
                      />
                      <span className="search-shortcut">⌘ K</span>
                    </div>

                    {!projectSearchTerm && !showAllProjects ? (
                      <>
                        <div className="dropdown-content">
                          {/* Recent Projects */}
                          {recentProjects.length > 0 && (
                            <div className="dropdown-section">
                              <div className="dropdown-section-header">Recent Projects</div>
                              {recentProjects.map(project => {
                                const pendingCount = projectPendingCounts[project.id] || 0
                                const isSelected = selectedProject?.id === project.id

                                return (
                                  <div
                                    key={project.id}
                                    className={`project-card ${isSelected ? 'selected' : ''}`}
                                    onClick={() => {
                                      selectProject(project)
                                      setProjectSearchOpen(false)
                                      setProjectSearchTerm('')
                                    }}
                                  >
                                    <div className="project-info">
                                      <div className="project-name">{project.name}</div>
                                      <div className="project-category">{getProjectCategory(project.name)}</div>
                                    </div>
                                    {pendingCount > 0 && (
                                      <span className="project-badge">{pendingCount}</span>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}

                          {/* All Projects Preview */}
                          <div className="dropdown-section dropdown-section-middle">
                            <div className="dropdown-section-header">
                              <span>All Projects ({projects.length})</span>
                              <button
                                className="view-all-link"
                                onClick={() => setShowAllProjects(true)}
                              >
                                View All
                              </button>
                            </div>
                            {projects.slice(0, 6).map(project => {
                              const pendingCount = projectPendingCounts[project.id] || 0
                              const isSelected = selectedProject?.id === project.id

                              return (
                                <div
                                  key={project.id}
                                  className={`project-list-item ${isSelected ? 'selected' : ''}`}
                                  onClick={() => {
                                    selectProject(project)
                                    setProjectSearchOpen(false)
                                    setProjectSearchTerm('')
                                  }}
                                >
                                  <span className="project-list-name">{project.name}</span>
                                  {pendingCount > 0 && (
                                    <span className="project-list-badge">{pendingCount}</span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="searchable-select-options">
                        {showAllProjects && (
                          <button
                            className="back-button"
                            onClick={() => setShowAllProjects(false)}
                          >
                            ← Back
                          </button>
                        )}
                        {filteredProjects.map(project => {
                          const pendingCount = projectPendingCounts[project.id] || 0
                          const isSelected = selectedProject?.id === project.id

                          return (
                            <div
                              key={project.id}
                              className={`searchable-select-option ${isSelected ? 'selected' : ''}`}
                              onClick={() => {
                                selectProject(project)
                                setProjectSearchOpen(false)
                                setProjectSearchTerm('')
                                setShowAllProjects(false)
                              }}
                            >
                              <span className="option-name">{project.name}</span>
                              {pendingCount > 0 && (
                                <span key={`badge-${project.id}-${pendingCount}`} className="option-badge">{pendingCount}</span>
                              )}
                            </div>
                          )
                        })}
                        {filteredProjects.length === 0 && (
                          <div className="searchable-select-option no-results">
                            No projects found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location Selector */}
          {user && selectedProject && locations.length > 0 && (
            <div className="location-selector">
              <label htmlFor="location-select" className="location-label">
                Location:
              </label>
              <select
                id="location-select"
                className="location-select"
                value={selectedLocation?.id || 'all'}
                onChange={(e) => {
                  if (e.target.value === 'all') {
                    selectLocation(null)
                  } else {
                    const loc = locations.find(l => l.id === e.target.value)
                    selectLocation(loc)
                  }
                }}
                disabled={locationsLoading}
              >
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Notification Bell */}
          {user && (
            <button className="notification-button" type="button">
              <svg className="notification-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z" fill="currentColor"/>
              </svg>
              {totalNotifications > 0 && (
                <span className="notification-badge">{totalNotifications}</span>
              )}
            </button>
          )}

          {/* Welcome Message */}
          {user && (
            <span className="user-welcome">
              Welcome, {user.name || user.preferred_username || 'User'}!
            </span>
          )}

          {/* User Profile Dropdown */}
          {user && (
            <div className="user-profile-container" ref={userDropdownRef}>
              <button
                className="user-profile-button"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                type="button"
              >
                <span className="user-initials">{getUserInitials()}</span>
                <span className="user-dropdown-arrow">▼</span>
              </button>

              {userDropdownOpen && (
                <div className="user-dropdown-menu">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-initials">{getUserInitials()}</div>
                    <div className="user-dropdown-info">
                      <div className="user-dropdown-name">{user.name || user.preferred_username || 'User'}</div>
                      <div className="user-dropdown-email">{user.email || ''}</div>
                    </div>
                  </div>
                  <div className="user-dropdown-divider"></div>
                  <div
                    onMouseDown={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      onLogout()
                    }}
                    className="user-dropdown-item"
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: 10000,
                      pointerEvents: 'all'
                    }}
                  >
                    <span className="user-dropdown-icon">🚪</span>
                    <span>Sign Out</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navigation Row */}
      <div className="mobile-nav-row">
        {/* Project Name Display for Mobile */}
        <div className="mobile-project-display">
          <span className="mobile-project-name">{selectedProject?.name || 'Select Project'}</span>
        </div>

        {/* User Avatar with Dropdown */}
        <div className="mobile-user-container" ref={userDropdownRef}>
          <div onClick={() => setUserDropdownOpen(!userDropdownOpen)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <div className="mobile-user-avatar">
              {getUserInitials()}
            </div>
            <span className="mobile-user-dropdown-arrow">▼</span>
          </div>

          {/* Mobile User Dropdown Menu */}
          {userDropdownOpen && (
            <div className="mobile-user-dropdown-menu" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-user-dropdown-header">
                <div className="mobile-user-dropdown-initials">{getUserInitials()}</div>
                <div className="mobile-user-dropdown-info">
                  <div className="mobile-user-dropdown-name">{user?.name || user?.preferred_username || 'User'}</div>
                  <div className="mobile-user-dropdown-email">{user?.email || ''}</div>
                </div>
              </div>
              <div className="mobile-user-dropdown-divider"></div>
              <div
                onMouseDown={(e) => {
                  console.log('[DashboardNav] Mobile sign out mouse down!')
                  e.stopPropagation()
                  e.preventDefault()
                  console.log('[DashboardNav] Calling onLogout...')
                  onLogout()
                }}
                className="mobile-user-dropdown-item"
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 10000,
                  pointerEvents: 'all'
                }}
              >
                <span className="mobile-user-dropdown-icon">🚪</span>
                <span>Sign Out</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default DashboardNav
